import { useCartAmount, useShoppingcartStore } from "@features/cart/shopping-cart-store";
import type { ProductType } from "@type/global";
import { useAuth } from "@provider/context-auth";
import * as React from "react";

import Amount from "./amount";
import NotAuthenticatedModal from "./not-authenticated-modal";
import type { Statiegeld } from "@type/statiegeld";
import { Text, useSx } from "dripsy";
import AnimatedPressable from "./animated-pressable";

interface ProductAmountType {
  product: ProductType;
  statiegeld?: Statiegeld;
  shouldStretch?: boolean;
  recipeId?: number;
  fallback?: React.ReactNode;
  isDetailView?: boolean;
}

function ProductAmount({
  product,
  statiegeld,
  shouldStretch,
  recipeId,
  fallback,
  isDetailView = false,
}: ProductAmountType) {
  const [modalVisible, setModalVisible] = React.useState(false);
  const { token, isAuthenticated } = useAuth();
  const addOrUpdateProduct = useShoppingcartStore((store) => store.addOrUpdateProduct);
  const amount = useCartAmount(product.id, recipeId);
  const hasDiscount = product.actie > 0 || product.actieprijs > 0;

  const allDisposed = statiegeld && amount === statiegeld.aant;
  const maxAmount = statiegeld ? statiegeld.aant : product.voorraad;

  const [stockError, setStockError] = React.useState<string | null>(null);

  const changeAmount = React.useCallback(
    (value: number) => {
      if (isAuthenticated) {
        // If statiegeld and max is 0, remove from cart
        if (statiegeld && maxAmount === 0) {
          setStockError("Geen statiegeld beschikbaar");
          setTimeout(() => setStockError(null), 3000);
          // Set to 0 to remove from cart
          addOrUpdateProduct(product, 0, token ?? "", recipeId);
          return;
        }

        // Check voorraad/statiegeld limiet - only if maxAmount is not null
        if (maxAmount !== null && maxAmount !== undefined && value > maxAmount) {
          // Show inline feedback instead of toast
          if (statiegeld) {
            setStockError(`Maximaal ${maxAmount} statiegeld mogelijk`);
          } else if (maxAmount === 0) {
            setStockError("Dit product is uitverkocht");
          } else if (amount >= maxAmount) {
            // User is already at max and trying to add more
            if (isDetailView) {
              setStockError(`Niet meer dan ${maxAmount} op voorraad`);
            } else {
              setStockError("Maximale voorraad bereikt");
            }
          } else {
            setStockError(`Maximaal ${maxAmount} beschikbaar`);
          }
          // Clear error after 3 seconds
          setTimeout(() => setStockError(null), 3000);
          // Don't update the value
          return;
        }

        // Clear any existing error
        setStockError(null);
        addOrUpdateProduct(product, value, token ?? "", recipeId);
      } else {
        setModalVisible(true);
      }
    },
    [isAuthenticated, addOrUpdateProduct, product, token, recipeId, maxAmount, statiegeld, amount, isDetailView],
  );
  const sx = useSx();

  // Check if stock is limited and user is approaching the limit
  // Show for both voorraad and statiegeld when there's a limit
  const showStockInfo =
    maxAmount !== null && maxAmount !== undefined && maxAmount > 0 && amount > 0 && amount < maxAmount;
  const remainingStock = showStockInfo ? Math.max(0, maxAmount - amount) : 0;

  return (
    <>
      {showStockInfo && !stockError && (
        <Text
          selectable={false}
          sx={{
            fontSize: isDetailView ? 13 : 11,
            color: remainingStock <= 3 ? "$warning" : "$textSecondary",
            fontWeight: isDetailView ? "500" : "400",
            paddingHorizontal: 8,
          }}
        >
          {remainingStock <= 3 ? `Nog ${remainingStock} beschikbaar` : `Nog ${maxAmount - amount} beschikbaar`}
        </Text>
      )}
      <Amount
        shouldStretch={shouldStretch}
        value={amount ?? 0}
        onChange={changeAmount}
        discount={hasDiscount}
        fallback={fallback}
      />
      {stockError && (
        <Text
          selectable={false}
          sx={{
            fontSize: 12,
            color: "$error",
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 4,
          }}
        >
          {stockError}
        </Text>
      )}
      {statiegeld && (
        <AnimatedPressable
          // hitSlop={hitslop}
          style={sx({
            paddingVertical: 3,
            paddingHorizontal: 12,
            borderRadius: 6,
            alignSelf: "flex-end",
            justifyContent: "center",
            alignItems: "center",
            borderWidth: 1,
            borderColor: "$lightGrey",
          })}
          onPress={() => {
            changeAmount(statiegeld.aant);
          }}
          animate={({ hovered, pressed }) => {
            return {
              opacity: statiegeld.aant === amount ? 0 : 1,
              transform: [{ scale: hovered || pressed ? 1.05 : 1 }],
            };
          }}
          accessibilityRole="button"
        >
          <Text
            selectable={false}
            sx={{
              fontWeight: "500",
            }}
          >
            {allDisposed ? "Alles ingeleverd" : `Alles inleveren`}
          </Text>
        </AnimatedPressable>
      )}

      <NotAuthenticatedModal isVisible={modalVisible} onClose={() => setModalVisible(false)} />
    </>
  );
}

export default ProductAmount;
